import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, model, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, switchMap } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { SelectModule } from 'primeng/select';
import { CheckboxModule } from 'primeng/checkbox';
import { DividerModule } from 'primeng/divider';
import { TagModule } from 'primeng/tag';
import { PessoaAvatarComponent } from '@/shared/components/pessoa/pessoa-avatar/pessoa-avatar';
import { PessoaFotoDialogComponent } from '@/shared/components/pessoa/pessoa-foto-dialog/pessoa-foto-dialog';
import { Tabs, TabList, Tab, TabPanels, TabPanel } from 'primeng/tabs';
import { TableModule } from 'primeng/table';
import { FormsModule } from '@angular/forms';
import { EstruturaService } from '@/core/api/sicom-api/sistema/estrutura/estrutura.service';
import { NotificacoesService } from '@/core/services/notificacoes.service';
import { CoordenadorResponse, EstruturaResponse, FuncionarioResponse } from '@/core/api/sicom-api/sistema/estrutura/estrutura.model';
import { FuncionarioVincularRequest } from '@/core/api/sicom-api/sistema/estrutura/estrutura.model';
import { PesquisaCoordenador } from '../../pesquisa-coordenador/pesquisa-coordenador';
import { iniciais } from '@/shared/utils/nome-utils';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
type Passo = 'form' | 'buscar-coordenador' | 'buscar-funcionario';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-estrutura-detalhe',
    standalone: true,
    imports: [
        DialogoPadraoDirective,
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        ButtonModule,
        Dialog,
        InputTextModule,
        IconFieldModule,
        InputIconModule,
        SelectModule,
        CheckboxModule,
        DividerModule,
        TagModule,
        PessoaAvatarComponent,
        PessoaFotoDialogComponent,
        Tabs,
        TabList,
        Tab,
        TabPanels,
        TabPanel,
        TableModule,
        PesquisaCoordenador,
        CanDirective
    ],
    templateUrl: './estrutura-detalhe.html',
    styleUrl: './estrutura-detalhe.scss'
})
/** Modal de novo/edição de estrutura organizacional. */
export class EstruturaDetalhe {
    protected readonly roleEstruturasCriar = PERMISSOES.configuracoes.estruturas.criar;
    protected readonly roleEstruturasEditar = PERMISSOES.configuracoes.estruturas.editar;
    protected readonly roleEstruturasAlterarStatus = PERMISSOES.configuracoes.estruturas.alterarStatus;

    private readonly fb = inject(FormBuilder);
    private readonly service = inject(EstruturaService);
    private readonly notificacoes = inject(NotificacoesService);
    private readonly destroyRef = inject(DestroyRef);

    estrutura = input<EstruturaResponse | null>(null);
    todasEstruturas = input<EstruturaResponse[]>([]);
    showDialog = model.required<boolean>();
    salvo = output<void>();

    protected passo = signal<Passo>('form');
    protected salvando = signal(false);
    protected carregandoFuncionarios = signal(false);
    protected vinculando = signal(false);
    protected removendoId = signal<string | null>(null);
    protected abaAtiva = signal<string | number | undefined>('0');
    protected formSubmitted = false;

    protected coordenadorSelecionado = signal<CoordenadorResponse | null>(null);
    protected funcionarios = signal<FuncionarioResponse[]>([]);
    protected filtroFuncionarios = signal('');
    protected funcionariosFiltrados = computed(() => {
        const termo = this.filtroFuncionarios().toLowerCase().trim();
        if (!termo) return this.funcionarios();
        return this.funcionarios().filter((f) => f.nome?.toLowerCase().includes(termo) || f.cpf?.toLowerCase().includes(termo) || f.uf?.toLowerCase().includes(termo));
    });

    protected form!: FormGroup<{
        descricao: FormControl<string>;
        idEoSuperior: FormControl<number | null>;
        ativo: FormControl<number>;
        inCompartilharProtocolo: FormControl<boolean>;
    }>;

    private readonly carregarFuncionarios$ = new Subject<number>();

    protected readonly opcoesStatus = [
        { label: 'Ativo', value: 1 },
        { label: 'Inativo', value: 0 }
    ];

    protected readonly opcoesSuperiores = computed(() =>
        this.todasEstruturas()
            .filter((e) => e.id !== this.estrutura()?.id)
            .map((e) => ({ label: `#${e.id} — ${e.descricao}`, value: e.id }))
    );

    constructor() {
        this.carregarFuncionarios$
            .pipe(
                switchMap((id) => {
                    this.carregandoFuncionarios.set(true);
                    return this.service.listarFuncionarios(id);
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: (f) => {
                    this.funcionarios.set(f);
                    this.carregandoFuncionarios.set(false);
                },
                error: () => this.carregandoFuncionarios.set(false)
            });

        effect(() => {
            if (this.showDialog()) {
                this.inicializar();
            }
        });
    }

    get modoEdicao(): boolean {
        return this.estrutura() !== null;
    }

    get titulo(): string {
        return this.modoEdicao ? 'Editar Estrutura' : 'Nova Estrutura';
    }

    private inicializar(): void {
        this.formSubmitted = false;
        this.salvando.set(false);
        this.passo.set('form');
        this.abaAtiva.set('0');
        this.funcionarios.set([]);
        const e = this.estrutura();

        this.form = this.fb.nonNullable.group({
            descricao: [e?.descricao ?? '', [Validators.required, Validators.maxLength(100)]],
            idEoSuperior: new FormControl<number | null>(e?.idEoSuperior ?? null),
            ativo: [e?.ativo ?? 1, Validators.required],
            inCompartilharProtocolo: [e?.inCompartilharProtocolo === 'S']
        });

        if (e?.idSolicitanteCoordenador) {
            this.coordenadorSelecionado.set({
                idSolicitante: e.idSolicitanteCoordenador,
                uf: e.sgUfCoordenador ?? '',
                nome: e.coordenador ?? '',
                cpf: '',
                email: '',
                avatar: e.avatarCoordenador ?? undefined
            });
        } else {
            this.coordenadorSelecionado.set(null);
        }

        if (this.modoEdicao && e) {
            this.carregarFuncionarios(e.id);
        }
    }

    private carregarFuncionarios(id: number): void {
        this.carregarFuncionarios$.next(id);
    }

    protected abrirBuscaCoordenador(): void {
        this.passo.set('buscar-coordenador');
    }

    protected onCoordenadorSelecionado(c: CoordenadorResponse): void {
        this.coordenadorSelecionado.set(c);
        this.passo.set('form');
    }

    protected removerCoordenador(): void {
        this.coordenadorSelecionado.set(null);
    }

    protected abrirBuscaFuncionario(): void {
        this.passo.set('buscar-funcionario');
    }

    protected onFuncionarioSelecionado(c: CoordenadorResponse): void {
        const id = this.estrutura()?.id;
        if (!id) return;
        const request: FuncionarioVincularRequest = { idSolicitante: c.idSolicitante, uf: c.uf };
        this.vinculando.set(true);
        this.service
            .vincularFuncionario(id, request)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    this.vinculando.set(false);
                    this.passo.set('form');
                    this.abaAtiva.set('1');
                    this.carregarFuncionarios(id);
                    this.notificacoes.notificar({ severity: 'success', summary: 'Funcionário vinculado com sucesso.' });
                },
                error: () => {
                    this.vinculando.set(false);
                    this.notificacoes.notificar({ toast: false, severity: 'error', summary: 'Erro ao vincular funcionário.' });
                }
            });
    }

    protected desvincularFuncionario(func: FuncionarioResponse): void {
        const id = this.estrutura()?.id;
        if (!id) return;
        const key = `${func.idSolicitante}_${func.uf}`;
        this.removendoId.set(key);
        this.service
            .desvincularFuncionario(id, func.idSolicitante, func.uf)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    this.removendoId.set(null);
                    this.carregarFuncionarios(id);
                    this.notificacoes.notificar({ severity: 'success', summary: 'Funcionário removido com sucesso.' });
                },
                error: () => {
                    this.removendoId.set(null);
                    this.notificacoes.notificar({ toast: false, severity: 'error', summary: 'Erro ao remover funcionário.' });
                }
            });
    }

    protected isInvalid(campo: string): boolean {
        const c = this.form?.get(campo);
        return (c?.invalid && (c.dirty || c.touched || this.formSubmitted)) ?? false;
    }

    protected salvar(): void {
        this.formSubmitted = true;
        if (this.form.invalid) return;

        const fv = this.form.getRawValue();
        const coord = this.coordenadorSelecionado();
        const e = this.estrutura();

        const request = {
            descricao: fv.descricao,
            idEoSuperior: fv.idEoSuperior ?? null,
            ativo: fv.ativo,
            inCompartilharProtocolo: fv.inCompartilharProtocolo ? 'S' : 'N',
            idSolicitanteCoordenador: coord?.idSolicitante ?? null,
            sgUfCoordenador: coord?.uf ?? null
        };

        this.salvando.set(true);
        const op$ = e ? this.service.atualizar(e.id, request) : this.service.salvar(request);

        op$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: () => {
                this.notificacoes.notificar({
                    severity: 'success',
                    summary: e ? 'Estrutura atualizada' : 'Estrutura criada com sucesso.'
                });
                this.salvando.set(false);
                this.salvo.emit();
            },
            error: () => {
                this.notificacoes.notificar({ toast: false, severity: 'error', summary: 'Erro ao salvar estrutura' });
                this.salvando.set(false);
            }
        });
    }

    protected fechar(): void {
        this.showDialog.set(false);
    }

    protected readonly iniciaisCoordenador = iniciais;

    /** Foto em exibição no diálogo de ampliação; `null` o mantém fechado. */
    protected readonly fotoAmpliada = signal<string | null>(null);
    protected readonly nomeFotoAmpliada = signal('');

    /** Amplia a foto que o avatar emitiu, anunciando de quem ela é. */
    protected ampliarFoto(url: string, nome: string): void {
        this.nomeFotoAmpliada.set(nome ?? '');
        this.fotoAmpliada.set(url);
    }

    protected labelTipoFuncionario(tipo: number): string {
        const map: Record<number, string> = {
            1: 'Médico',
            2: 'Escola',
            3: 'Órgão',
            4: 'Outros PJ',
            5: 'Prestador',
            6: 'Outros PF',
            7: 'Funcionário',
            8: 'Aluno'
        };
        return map[tipo] ?? 'Outro';
    }
}
